import { clamp } from 'lodash';
import { useMemo } from 'react';
import { ZoomLevel } from '~/routes/calendar._index';

// Named constants for shared magic numbers
export const EXPANDED_BOTTOM_PADDING = 42;
export const OUTLET_WIDTH = 414;
export const SCROLL_MARGIN = 50;
export const DRAG_HOVER_EXTRA_HEIGHT = 48;
export const HEADER_HEIGHT = 56;
export const SIDEBAR_WIDTH = 333;
export const COLLAPSED_SIDEBAR_WIDTH = 83;
export const MOBILE_SIDEBAR_WIDTH = 333;
export const COLLAPSED_MOBILE_SIDEBAR_WIDTH = 66;

export type PlannerDimensions = {
  headerHeight: number;
  sideBarWidth: number;
  collapsedSideBarWidth: number;
  mobileSideBarWidth: number;
  collapsedMobileSideBarWidth: number;
  actualSideBarWidth: number;
  jobPillHeight: number;
  expandPillSize: number;
  overlappingPillOffset: number;
  jobPillUnderExpandPillMarginRight: number;
  expandedBookingPillHeight: number;
  bookingPillScaleFactor: number;
  bookingPillHeight: number;
  jobPillMargin: number;
  minRowHeight: number;
  columnWidth: number;
};

interface UsePlannerDimensionsOptions {
  isMobile: boolean;
  isSidebarExpanded: boolean;
  windowWidth: number;
  zoomLevel: ZoomLevel;
  jobDetailEffective: boolean;
  bookingsHiddenEffective: boolean;
}

/**
 * Custom hook that computes all dimension-related values for the planner.
 * Memoized to prevent recalculation on unrelated state changes.
 */
export function usePlannerDimensions({
  isMobile,
  isSidebarExpanded,
  windowWidth,
  zoomLevel,
  jobDetailEffective,
  bookingsHiddenEffective,
}: UsePlannerDimensionsOptions): PlannerDimensions {
  const actualSideBarWidth = useMemo(() => {
    return isMobile
      ? isSidebarExpanded
        ? MOBILE_SIDEBAR_WIDTH
        : COLLAPSED_MOBILE_SIDEBAR_WIDTH
      : isSidebarExpanded
      ? SIDEBAR_WIDTH
      : COLLAPSED_SIDEBAR_WIDTH;
  }, [isMobile, isSidebarExpanded]);

  const {
    jobPillHeight,
    expandPillSize,
    jobPillUnderExpandPillMarginRight,
    expandedBookingPillHeight,
    bookingPillScaleFactor,
    bookingPillHeight,
    jobPillMargin,
    minRowHeight,
  } = useMemo(() => {
    const computedJobPillHeight = jobDetailEffective ? 48 : 27;
    const computedExpandedBookingPillHeight = bookingsHiddenEffective ? 0 : 20;
    const computedBookingPillScaleFactor = 0.45;
    const computedJobPillMargin = 2;
    const computedBookingPillHeight =
      computedExpandedBookingPillHeight * computedBookingPillScaleFactor;

    return {
      jobPillHeight: computedJobPillHeight,
      expandPillSize: 23,
      jobPillUnderExpandPillMarginRight: 8,
      expandedBookingPillHeight: computedExpandedBookingPillHeight,
      bookingPillScaleFactor: computedBookingPillScaleFactor,
      bookingPillHeight: computedBookingPillHeight,
      jobPillMargin: computedJobPillMargin,
      minRowHeight:
        computedBookingPillHeight +
        computedJobPillMargin +
        computedJobPillHeight +
        computedJobPillMargin,
    };
  }, [jobDetailEffective, bookingsHiddenEffective]);

  const overlappingPillOffset = useMemo(() => {
    const zoomLevelsToOverlappingPillOffset: Record<ZoomLevel, number> = {
      month: 20,
      default: 25,
      week: isMobile
        ? clamp(((windowWidth - actualSideBarWidth) / 1.5) * 0.3, 20, 40)
        : clamp(((windowWidth - SIDEBAR_WIDTH) / 7) * 0.3, 20, 40),
    };
    return zoomLevelsToOverlappingPillOffset[zoomLevel];
  }, [zoomLevel, isMobile, windowWidth, actualSideBarWidth]);

  const columnWidth = useMemo(() => {
    const zoomLevelsToColumnWidth: Record<ZoomLevel, number> = {
      month: 59,
      default: 100,
      week: isMobile
        ? (windowWidth - actualSideBarWidth) / 1.5
        : (windowWidth - SIDEBAR_WIDTH) / 7,
    };
    return zoomLevelsToColumnWidth[zoomLevel];
  }, [zoomLevel, isMobile, windowWidth, actualSideBarWidth]);

  return {
    headerHeight: HEADER_HEIGHT,
    sideBarWidth: SIDEBAR_WIDTH,
    collapsedSideBarWidth: COLLAPSED_SIDEBAR_WIDTH,
    mobileSideBarWidth: MOBILE_SIDEBAR_WIDTH,
    collapsedMobileSideBarWidth: COLLAPSED_MOBILE_SIDEBAR_WIDTH,
    actualSideBarWidth,
    jobPillHeight,
    expandPillSize,
    overlappingPillOffset,
    jobPillUnderExpandPillMarginRight,
    expandedBookingPillHeight,
    bookingPillScaleFactor,
    bookingPillHeight,
    jobPillMargin,
    minRowHeight,
    columnWidth,
  };
}
